import React, { useMemo } from 'react';
import { DocumentModel, DocumentSection } from '../ast/document.types';
import { PageSheet } from './PageSheet';
import { SectionRenderer, ManualEditRenderOptions } from './SectionRenderer';

interface DocumentRendererProps {
  document: DocumentModel;
  className?: string;
  manualEditOptions?: ManualEditRenderOptions;
}

export const DocumentRenderer: React.FC<DocumentRendererProps> = ({
  document: doc,
  className = '',
  manualEditOptions,
}) => {
  // Partition sections across virtual pages (handling explicit page breaks and cover separation)
  const pages = useMemo(() => {
    const pageList: DocumentSection[][] = [[]];
    let currentPageIdx = 0;

    const enabledSections = doc.sections.filter((s) => s.enabled);

    enabledSections.forEach((sec, idx) => {
      // If a section specifies pageBreakBefore (or is after cover), advance to next page
      if (sec.settings?.pageBreakBefore || (idx > 0 && enabledSections[idx - 1].type === 'cover')) {
        if (pageList[currentPageIdx].length > 0) {
          pageList.push([]);
          currentPageIdx++;
        }
      }

      pageList[currentPageIdx].push(sec);

      // If the section contains a page_break block, trigger next page
      const hasForcedPageBreak = sec.blocks.some((b) => b.type === 'page_break');
      if (hasForcedPageBreak) {
        pageList.push([]);
        currentPageIdx++;
      }
    });

    // Clean empty trailing pages
    return pageList.filter((p) => p.length > 0);
  }, [doc.sections]);

  const totalPages = Math.max(1, pages.length);

  return (
    <div
      className={`doc-renderer-container ${className}`.trim()}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}
    >
      {pages.map((pageSections, pageIdx) => {
        let numberedSectionCounter = 1;

        return (
          <PageSheet
            key={pageIdx}
            pageNumber={pageIdx + 1}
            totalPages={totalPages}
            settings={doc.pageSettings}
          >
            {/* Render Sections in this Page */}
            {pageSections.map((sec) => {
              const currentNum = sec.type !== 'cover' ? numberedSectionCounter++ : undefined;
              return (
                <SectionRenderer
                  key={sec.id}
                  section={sec}
                  sectionNumber={currentNum}
                  manualEditOptions={manualEditOptions}
                />
              );
            })}
          </PageSheet>
        );
      })}
    </div>
  );
};
